import React, { useState, ReactElement } from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { Grid, useMediaQuery } from '@material-ui/core'
import clsx from 'clsx'
import CountUp from 'react-countup'
import FontSize from '@config/theme/definitions/fontSize'
import { Colors } from '@config/theme/palette'

import Headline from '@objects/headline'
import Icon from '@objects/icon'

import { useAutobahnplakateTexts } from '../store'
import EmailForm from './email-form'

const useStyles = makeStyles((theme) => ({
  root: {},
  ranking: {
    border: `1px solid #798414`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4, 6),
    boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#F9FFB9',
  },
  votingHeadline: {
    fontSize: FontSize['base'],
    margin: 0,
    textAlign: 'center',
  },
  votingInfo: {
    fontSize: FontSize['sm'],
    textAlign: 'center',
  },
  votingResults: {
    margin: theme.spacing(4, 0, 10, 0),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  topLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: theme.spacing(6, 0, 4, 0),
    height: theme.spacing(8),
  },
  topLineIconWrapper: {
    aspectRatio: '1 / 1',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    backgroundColor: theme.palette.background.default,
    overflow: 'hidden',
  },
  topLineIcon: {
    fontSize: '18px',
  },
  topLineLabel: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.spacing(5),
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(0, 6),
    fontSize: FontSize['xs'],
    margin: 0,
  },
  headline: {
    fontWeight: 900,
    fontSize: '1.5rem',
    textAlign: 'center',
    margin: 0,
    '& .highlight': {
      color: Colors.green[500],
    },

    [theme.breakpoints.up('md')]: {
      fontSize: '2rem',
    },

    [theme.breakpoints.up('lg')]: {
      fontSize: '2rem',
    },
  },
  mailContainer: {
    marginTop: theme.spacing(10),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(20),
    },
  },
}))

const useItemStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
  },
  headline: {
    fontSize: FontSize['base'],
    margin: 0,
    fontWeight: 500,
  },
  bar: {
    height: theme.spacing(12),
    backgroundColor: '#FDFFE6',
    borderRadius: theme.spacing(2),
    border: '1px solid #798414',
  },
  barFill: {
    position: 'relative',
    height: '100%',
    backgroundColor: Colors.green[500],
    borderRadius: theme.spacing(2),
    textAlign: 'right' as const,
    fontSize: '30px',
    fontFamily: theme.typography.fontFamily2,
    fontWeight: 900,
    color: theme.palette.common.white,
    width: '0%',
    transition: 'width 1s cubic-bezier(0, 0, 0.3, 1) 0.1s',

    '& span': {
      padding: theme.spacing(0, 2),
    },
  },
  barText: {
    position: 'absolute',
    top: '50%',
    transform: 'translate(0%, -50%)',
    right: 0,
  },
  rightAlign: {
    transform: 'translate(100%, -50%)',
    color: Colors.green[500],
  },
}))

function VotingResultItem({
  label,
  value,
}: {
  label: string
  value: number
}): ReactElement {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useItemStyles()
  const limit = isMobile ? 25 : 16

  return (
    <div className={classes.root}>
      <h4 className={classes.headline}>
        {label.charAt(0).toUpperCase() + label.slice(1)}
      </h4>
      <div className={classes.bar}>
        <div className={classes.barFill} style={{ width: `${value}%` }}>
          <CountUp
            end={value}
            suffix="%"
            duration={2}
            className={clsx(classes.barText, {
              [classes.rightAlign]: value < limit,
            })}
          />
        </div>
      </div>
    </div>
  )
}

export default function Result(): ReactElement {
  const classes = useStyles()
  const store = useAutobahnplakateTexts()
  const [done, setDone] = useState(false)
  const showVotingResults = Boolean(store.resultVotingHeadline)
  const hasRankingData = showVotingResults && store.results.length > 0

  const items = [
    store.votingItemLabel1,
    store.votingItemLabel2,
    store.votingItemLabel3,
    store.votingItemLabel4,
    store.votingItemLabel5,
  ]

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={8}
        justifyContent={!hasRankingData ? 'center' : undefined}
      >
        {hasRankingData && (
          <Grid item xs={12} md={6}>
            <div className={classes.ranking}>
              <h3 className={classes.votingHeadline}>
                {store.resultVotingHeadline}
              </h3>
              <div className={classes.votingResults}>
                {store.results.map((result, index) => (
                  <VotingResultItem
                    key={result.token}
                    label={items[index]}
                    value={result.count}
                  />
                ))}
              </div>
              <p className={classes.votingInfo}>{store.resultVotingInfo}</p>
            </div>
          </Grid>
        )}
        <Grid item xs={12} md={hasRankingData ? 6 : 8}>
          <div className={classes.topLine}>
            <div className={classes.topLineIconWrapper}>
              <Icon name="Happy" className={classes.topLineIcon} />
            </div>
            <h4 className={classes.topLineLabel}>{store.resultTopline}</h4>
          </div>

          <Headline level={2} className={classes.headline}>
            {done ? store.resultHeadlineSuccess : store.resultHeadline}
          </Headline>

          {!done && (
            <div className={classes.mailContainer}>
              <EmailForm setDone={setDone} />
            </div>
          )}
        </Grid>
      </Grid>
    </div>
  )
}
